import { useEffect, useRef, useState } from "react";

export const useVideoPlayer = ({ videoSrc, trimTimeStart, trimTimeEnd, crop }) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [muted, setMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(trimTimeStart ?? 0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
    
    // Load metadata and cropping
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleLoadedMetadata = () => {
            const vidDuration = video.duration;
            const validTrimStart = trimTimeStart >= 0 && trimTimeStart < vidDuration ? trimTimeStart : 0;
            const validTrimEnd = trimTimeEnd > validTrimStart && trimTimeEnd <= vidDuration ? trimTimeEnd : vidDuration;
            setDuration(validTrimEnd - validTrimStart);
            video.currentTime = validTrimStart;
            setCurrentTime(validTrimStart);

            setVideoDimensions({
                width: video.videoWidth,
                height: video.videoHeight,
            });
            video.pause();
            setIsPlaying(false);
        };

        const handleTimeUpdate = () => {
            if (trimTimeEnd && video.currentTime >= trimTimeEnd) {
                video.pause();
                video.currentTime = trimTimeStart;
                setCurrentTime(trimTimeStart);
                setIsPlaying(false);
            } else {
                setCurrentTime(video.currentTime);
            }
        };

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);

        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        video.addEventListener("timeupdate", handleTimeUpdate);
        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        return () => {
            video.removeEventListener("loadedmetadata", handleLoadedMetadata);
            video.removeEventListener("timeupdate", handleTimeUpdate);
            video.removeEventListener("play", handlePlay);
            video.removeEventListener("pause", handlePause);
        };
       

    }, [videoSrc, trimTimeStart, trimTimeEnd]);

    const togglePlayPause = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video.play();
        } else {
            video.pause();
        }
    };

    const toggleMute = () => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = !video.muted;
        setMuted(video.muted);
    };

    const toggleFullscreen = () => {
        const container = containerRef.current;
        if (!container) return;
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            container.requestFullscreen?.();
        }
    };

    const handleProgressChange = (e) => {
        const video = videoRef.current;
        if (!video) return;
        const validTrimStart = trimTimeStart >= 0 ? trimTimeStart : 0;
        const newTime = parseFloat(e.target.value) + validTrimStart;
        video.currentTime = newTime;
    };

    let cropStyles = {};
    if (crop?.croppedAreaPixels && videoDimensions.width && videoDimensions.height) {
        const { x, y, width: cropW, height: cropH } = crop.croppedAreaPixels;
        const scaleX = 100 / (cropW / videoDimensions.width);
        const scaleY = 100 / (cropH / videoDimensions.height);

        cropStyles = {
            width: `${scaleX}%`,
            height: `${scaleY}%`,
            transform: `translate(-${(x / videoDimensions.width) * 100}%, -${(y / videoDimensions.height) * 100}%)`,
            transformOrigin: "top left",
            position: "absolute",
            top: 0,
            left: 0,
            objectFit: "initial",
            borderRadius: "12px",
        };
    }

    const useCrop = !!(crop?.croppedAreaPixels && videoDimensions.width && videoDimensions.height);
    const validTrimStart = trimTimeStart >= 0 ? trimTimeStart : 0;
    const progressValue = currentTime - validTrimStart;

    return {
        videoRef,
        containerRef,
        muted,
        currentTime,
        duration,
        isPlaying,
        isHovered,
        setIsHovered,
        cropStyles,
        useCrop,
        togglePlayPause,
        toggleMute,
        toggleFullscreen,
        handleProgressChange,
        progressValue,
        validTrimStart,
        videoDimensions
    };
};
